import { useState, useEffect } from 'react';

interface Session {
  sessionId: string;
  startedAt: string;
  endedAt?: string;
  branch: string;
  claimedAreas: string[];
  currentTask: string;
  progress: string;
  lastUpdate: string;
  completedSteps?: string[];
  nextSteps?: string[];
  accomplishments?: string[];
  filesChanged?: string[];
  needsHandoff?: boolean;
  handoffMessage?: string;
  emergencyStop?: boolean;
  emergencyMessage?: string;
}

interface WorkAreaClaims {
  description: string;
  areas: Record<string, string | null>;
}

interface SessionStateData {
  project: string;
  lastUpdated: string;
  activeSessions: Session[];
  sessionHistory: Session[];
  sharedContext: {
    businessModel: string;
    currentPhase: string;
    deploymentStatus: Record<string, string>;
    mainBranch: string;
    protectedFiles: string[];
  };
  coordinationRules: {
    beforeStarting: string[];
    duringWork: string[];
    beforeEnding: string[];
  };
  workAreaClaims: WorkAreaClaims;
  communicationProtocol: Record<string, string>;
}

export default function SessionState() {
  const [sessionData, setSessionData] = useState<SessionStateData | null>(null);
  const [activeTab, setActiveTab] = useState<'active' | 'history' | 'context'>('active');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch session state data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // In production, this would fetch from an API
        // For now, we'll simulate with the static data
        const response = await fetch('/session-state.json');
        const data = await response.json();
        setSessionData(data);
      } catch (error) {
        console.error('Failed to load session state:', error);
      }
    };

    fetchData();

    // Auto-refresh every 30 seconds if enabled
    if (autoRefresh) {
      const interval = setInterval(fetchData, 30000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  if (!sessionData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading session state...</p>
        </div>
      </div>
    );
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProgressColor = (progress: string) => {
    const percent = parseInt(progress);
    if (percent < 30) return 'bg-red-500';
    if (percent < 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3">
                <a href="/#home" className="text-slate-400 hover:text-slate-600 transition-colors">
                  ← Back
                </a>
                <h1 className="text-2xl font-bold text-slate-900">Session State</h1>
                <span className="text-sm text-slate-500">
                  {sessionData.project}
                </span>
              </div>
              <p className="text-sm text-slate-600 mt-1">
                Last updated: {formatTimestamp(sessionData.lastUpdated)}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Auto-refresh
              </label>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Refresh Now
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Emergency Banner */}
        {sessionData.activeSessions.some(s => s.emergencyStop) && (
          <div className="mb-6 bg-red-50 border-2 border-red-500 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <span className="text-4xl">🚨</span>
              <div>
                <h3 className="text-xl font-bold text-red-900 mb-2">EMERGENCY STOP</h3>
                {sessionData.activeSessions
                  .filter(s => s.emergencyStop)
                  .map(s => (
                    <p key={s.sessionId} className="text-red-800 mb-2">
                      <strong>{s.sessionId}:</strong> {s.emergencyMessage}
                    </p>
                  ))}
                <p className="text-red-700 text-sm">
                  All sessions should halt work and read the emergency message above.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('active')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'active'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            Active Sessions ({sessionData.activeSessions.length})
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'history'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            Session History ({sessionData.sessionHistory.length})
          </button>
          <button
            onClick={() => setActiveTab('context')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'context'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            Context & Rules
          </button>
        </div>

        {/* Active Sessions Tab */}
        {activeTab === 'active' && (
          <div className="space-y-6">
            {sessionData.activeSessions.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <span className="text-6xl mb-4 block">💤</span>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">No Active Sessions</h3>
                <p className="text-slate-600 mb-6">
                  All quiet on the development front. Start a new session to get going!
                </p>
                <a
                  href="https://github.com/ideabrian/hello-reviews/blob/main/START-NEW-SESSION.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Read: How to Start a Session →
                </a>
              </div>
            ) : (
              sessionData.activeSessions.map((session) => (
                <div
                  key={session.sessionId}
                  className="bg-white rounded-xl p-6 border-2 border-blue-200"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">{session.sessionId}</h3>
                      <p className="text-sm text-slate-600">
                        Started: {formatTimestamp(session.startedAt)} • Last update: {formatTimestamp(session.lastUpdate)}
                      </p>
                    </div>
                    {session.needsHandoff && (
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                        Needs Handoff
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Branch</p>
                      <code className="text-sm bg-slate-100 px-3 py-1 rounded">{session.branch}</code>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Claimed Areas</p>
                      <div className="flex flex-wrap gap-2">
                        {session.claimedAreas.map((area) => (
                          <span
                            key={area}
                            className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                          >
                            {area}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-sm text-slate-600 mb-2">Current Task</p>
                    <p className="text-slate-900 font-medium">{session.currentTask}</p>
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-slate-600">Progress</p>
                      <span className="text-sm font-medium text-slate-900">{session.progress}</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div
                        className={`${getProgressColor(session.progress)} h-2 rounded-full transition-all`}
                        style={{ width: session.progress }}
                      ></div>
                    </div>
                  </div>

                  {session.completedSteps && session.completedSteps.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm text-slate-600 mb-2">Completed Steps</p>
                      <ul className="space-y-1">
                        {session.completedSteps.map((step, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                            <span className="text-green-600">✓</span>
                            {step}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {session.nextSteps && session.nextSteps.length > 0 && (
                    <div>
                      <p className="text-sm text-slate-600 mb-2">Next Steps</p>
                      <ul className="space-y-1">
                        {session.nextSteps.map((step, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                            <span className="text-slate-400">→</span>
                            {step}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {session.handoffMessage && (
                    <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm font-medium text-yellow-900 mb-1">Handoff Request</p>
                      <p className="text-sm text-yellow-800">{session.handoffMessage}</p>
                    </div>
                  )}
                </div>
              ))
            )}

            {/* Work Area Claims */}
            <div className="bg-white rounded-xl p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Work Area Claims</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(sessionData.workAreaClaims.areas).map(([area, claimedBy]) => (
                  <div
                    key={area}
                    className={`p-4 rounded-lg border-2 ${
                      claimedBy
                        ? 'bg-red-50 border-red-200'
                        : 'bg-green-50 border-green-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-900">{area}</span>
                      {claimedBy ? (
                        <span className="text-sm text-red-700">🔒 {claimedBy}</span>
                      ) : (
                        <span className="text-sm text-green-700">✓ Available</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Session History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            {sessionData.sessionHistory.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <span className="text-6xl mb-4 block">📝</span>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">No Session History Yet</h3>
                <p className="text-slate-600">
                  Completed sessions will appear here for reference.
                </p>
              </div>
            ) : (
              sessionData.sessionHistory.map((session) => (
                <div
                  key={session.sessionId}
                  className="bg-white rounded-xl p-6 border border-slate-200"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{session.sessionId}</h3>
                      <p className="text-sm text-slate-600">
                        {formatTimestamp(session.startedAt)} → {session.endedAt && formatTimestamp(session.endedAt)}
                      </p>
                    </div>
                    <code className="text-sm bg-slate-100 px-3 py-1 rounded">{session.branch}</code>
                  </div>

                  {session.accomplishments && session.accomplishments.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm text-slate-600 mb-2">Accomplishments</p>
                      <ul className="space-y-1">
                        {session.accomplishments.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                            <span className="text-green-600">✓</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {session.filesChanged && session.filesChanged.length > 0 && (
                    <div>
                      <p className="text-sm text-slate-600 mb-2">Files Changed</p>
                      <div className="flex flex-wrap gap-2">
                        {session.filesChanged.map((file, idx) => (
                          <code key={idx} className="text-xs bg-slate-100 px-2 py-1 rounded">
                            {file}
                          </code>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Context & Rules Tab */}
        {activeTab === 'context' && (
          <div className="space-y-6">
            {/* Shared Context */}
            <div className="bg-white rounded-xl p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Shared Context</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Business Model</p>
                  <p className="text-slate-900">{sessionData.sharedContext.businessModel}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-1">Current Phase</p>
                  <p className="text-slate-900">{sessionData.sharedContext.currentPhase}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-1">Main Branch</p>
                  <code className="text-sm bg-slate-100 px-3 py-1 rounded">
                    {sessionData.sharedContext.mainBranch}
                  </code>
                </div>
              </div>
            </div>

            {/* Deployment Status */}
            <div className="bg-white rounded-xl p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Deployment Status</h3>
              <div className="space-y-3">
                {Object.entries(sessionData.sharedContext.deploymentStatus).map(([key, value]) => (
                  <div key={key} className="flex items-start gap-3">
                    <span className="text-lg">
                      {value.includes('LIVE') || value.includes('registered') ? '✅' : '⏳'}
                    </span>
                    <div>
                      <p className="font-medium text-slate-900">{key}</p>
                      <p className="text-sm text-slate-600">{value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Coordination Rules */}
            <div className="bg-white rounded-xl p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Coordination Rules</h3>

              <div className="mb-6">
                <h4 className="font-medium text-slate-900 mb-3">Before Starting</h4>
                <ol className="space-y-2">
                  {sessionData.coordinationRules.beforeStarting.map((rule, idx) => (
                    <li key={idx} className="text-sm text-slate-700">{rule}</li>
                  ))}
                </ol>
              </div>

              <div className="mb-6">
                <h4 className="font-medium text-slate-900 mb-3">During Work</h4>
                <ol className="space-y-2">
                  {sessionData.coordinationRules.duringWork.map((rule, idx) => (
                    <li key={idx} className="text-sm text-slate-700">{rule}</li>
                  ))}
                </ol>
              </div>

              <div>
                <h4 className="font-medium text-slate-900 mb-3">Before Ending</h4>
                <ol className="space-y-2">
                  {sessionData.coordinationRules.beforeEnding.map((rule, idx) => (
                    <li key={idx} className="text-sm text-slate-700">{rule}</li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Protected Files */}
            <div className="bg-white rounded-xl p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Protected Files</h3>
              <p className="text-sm text-slate-600 mb-3">
                These files require careful coordination when editing:
              </p>
              <div className="flex flex-wrap gap-2">
                {sessionData.sharedContext.protectedFiles.map((file) => (
                  <code key={file} className="text-sm bg-red-50 border border-red-200 px-3 py-1 rounded">
                    {file}
                  </code>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white">
              <h3 className="text-xl font-bold mb-4">Quick Links</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <a
                  href="https://github.com/ideabrian/hello-reviews/blob/main/START-NEW-SESSION.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 hover:bg-white/20 rounded-lg p-4 transition-colors"
                >
                  <p className="font-medium mb-1">Start New Session</p>
                  <p className="text-sm text-blue-100">Quick 2-min guide</p>
                </a>
                <a
                  href="https://github.com/ideabrian/hello-reviews/blob/main/SESSION-COORDINATION.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 hover:bg-white/20 rounded-lg p-4 transition-colors"
                >
                  <p className="font-medium mb-1">Full Protocol</p>
                  <p className="text-sm text-blue-100">Complete 20-min read</p>
                </a>
                <a
                  href="https://github.com/ideabrian/hello-reviews/blob/main/HANDOFF.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 hover:bg-white/20 rounded-lg p-4 transition-colors"
                >
                  <p className="font-medium mb-1">Project Handoff</p>
                  <p className="text-sm text-blue-100">Current project state</p>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
